import { faCheckCircle } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  PLATFORM_MODE_TRADE, TRADE_DIRECTION_BUY, TRADE_DIRECTION_SELL,
  TRADE_DIRECTION_UNSPECIFIED, TRADE_RULE_STATE_ACCEPTED,
} from 'src/util/constants';
import { buildTradePointInlineLabel } from 'src/util/i18n/tradePoint';
import { getPriceRange } from 'src/util/tradeRule';

import { buildTradeRulePriceInformation } from 'src/util/i18n/tradeRule';
import TradeRuleTimelineStep from './TradeRuleTimelineStep';

/**
 * Description
 * @param {any} props
 * @returns {any} - TradeRuleTimelineStepAccepted component
 */
function TradeRuleTimelineStepAccepted(props) {
  const {
    buyer, clauses, hasNextStep, isCurrentStep, metersMap, propertyTimeZone,
    ruleId, seller, timestamp, tradeType, user,
  } = props;

  const intl = useIntl();

  let description;
  let direction;
  if (buyer.tradePoint.id in metersMap) {
    description = (
      <FormattedMessage
        id="trade_rule.trade_rule_timeline.trade_rule_timeline_step_accepted.buy.details"
        defaultMessage="To buy for {buyerTradepoint} from {sellerTradepoint}"
        values={{
          buyerTradepoint: buildTradePointInlineLabel(intl, buyer.tradePoint),
          sellerTradepoint: buildTradePointInlineLabel(intl, seller.tradePoint),
        }}
      />
    );
    direction = TRADE_DIRECTION_BUY;
  } else if (seller.tradePoint.id in metersMap) {
    description = (
      <FormattedMessage
        id="trade_rule.trade_rule_timeline.trade_rule_timeline_step_accepted.trade_direction_sell.details"
        defaultMessage="To sell from {sellerTradepoint} to {buyerTradepoint}"
        values={{
          buyerTradepoint: buildTradePointInlineLabel(intl, buyer.tradePoint),
          sellerTradepoint: buildTradePointInlineLabel(intl, seller.tradePoint),
        }}
      />
    );
    direction = TRADE_DIRECTION_SELL;
  } else {
    description = (
      <FormattedMessage
        id="trade_rule.trade_rule_timeline.trade_rule_timeline_step_accepted.trade_direction_unspecified.details"
        defaultMessage="To buy for {buyerTradepoint} from {sellerTradepoint}"
        values={{
          buyerTradepoint: buildTradePointInlineLabel(intl, buyer.tradePoint),
          sellerTradepoint: buildTradePointInlineLabel(intl, seller.tradePoint),
        }}
      />
    );
    direction = TRADE_DIRECTION_UNSPECIFIED;
  }

  const priceRange = getPriceRange({ clauses });
  const priceInformation = buildTradeRulePriceInformation({
    direction,
    intl,
    maximum: priceRange.maximum,
    minimum: priceRange.minimum,
    mode: PLATFORM_MODE_TRADE,
    timeline: true,
  });

  return (
    <TradeRuleTimelineStep
      hasNextStep={hasNextStep}
      icon={<FontAwesomeIcon icon={faCheckCircle} />}
      isCurrentStep={isCurrentStep}
      ruleId={ruleId}
      state={TRADE_RULE_STATE_ACCEPTED}
      timestamp={timestamp}
      timezone={propertyTimeZone}
      tradeType={tradeType}
      user={user}
    >
      <li className="mb-3">
        {description}
      </li>
      <li className="mb-3">
        {priceInformation}
        {' '}
      </li>
    </TradeRuleTimelineStep>
  );
}

TradeRuleTimelineStepAccepted.propTypes = {
  buyer: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  clauses: PropTypes.shape({
    edges: PropTypes.arrayOf(PropTypes.shape({
      node: (PropTypes.shape({
        price: PropTypes.number.isRequired,
      })),
    })),
  }).isRequired,
  hasNextStep: PropTypes.bool.isRequired,
  isCurrentStep: PropTypes.bool.isRequired,
  metersMap: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  propertyTimeZone: PropTypes.string.isRequired,
  ruleId: PropTypes.string.isRequired,
  seller: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  timestamp: PropTypes.number.isRequired,
  tradeType: PropTypes.string.isRequired,
  user: PropTypes.shape({
    id: PropTypes.string,
    email: PropTypes.string,
    givenName: PropTypes.string,
    familyName: PropTypes.string,
  }).isRequired,
};

TradeRuleTimelineStepAccepted.defaultProps = {
};

export default TradeRuleTimelineStepAccepted;
